'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X, type LucideIcon } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { isToastDockEvent } from '../overlayStack';
import { ToastSurface } from '../toastSurface';
import { Button } from './button';

/** The shadcn/ui Dialog, on Radix `@radix-ui/react-dialog`.
 *
 *  Radix owns what a dialog owes the keyboard and the screen reader: the focus trap, Tab looping,
 *  Escape, layer ordering among several open dialogs, and the `data-state` the animations key off.
 *  None of that is reimplemented here.
 *
 *  What is NOT Radix's, and survives on top of it, is this app's overlay policy —
 *  `components/ui/overlayStack.ts` (stack ownership, `inert` isolation, scroll lock, focus return) and
 *  `components/ui/overlayDepth.tsx` (nesting depth and the phone presentation). `components/ui/Modal.tsx`
 *  is the app-shaped wrapper that binds the two together and is what the app imports.
 *
 *  TWO DELIBERATE DEPARTURES FROM STOCK shadcn, both of which `shadcn add dialog` will undo:
 *
 *  1. NO `Dialog.Portal`. The app portals the whole overlay itself, once, at `DialogOverlay` (see
 *     `Modal.tsx`). Radix's portal would reparent the CONTENT alone into a second, separate child of
 *     <body>, which splits the surface from the backdrop that positions it and hands `overlayStack` two
 *     body children per overlay — it would mark the loose one `inert` as "not the top of the stack".
 *     Menus opened INSIDE a dialog are not portaled either (`select.tsx` carries the same rule), so they
 *     stay within the dialog's focus scope instead of being pulled out of it.
 *  2. NO `Dialog.Overlay` for the backdrop. Radix's overlay is a second dismissable surface with a
 *     scroll lock of its own; the app already locks the body from the overlay stack, and the backdrop
 *     here carries app policy Radix has no notion of (which presentation it is laying out, and that a
 *     nested dialog's backdrop must not also close its parent). */

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

/** The layer an overlay paints on: the scrim, a z-index band from the shared scale, and the box that
 *  positions the surface.
 *
 *  Both variant axes are app-owned. `Modal.tsx` resolves `presentation` from an explicit override or
 *  overlay depth and viewport. It derives `layer` from that shape: drawer on the drawer band, other
 *  shapes on the modal band, with page stand-ins on the lower page band. Intent does not change either. */
const dialogOverlayVariants = cva('fixed inset-0 flex', {
  variants: {
    presentation: {
      center: 'items-center justify-center',
      drawer: 'justify-end',
      // A sheet and a fullscreen surface are laid out by `.overlay-surface[data-presentation]`, which
      // owns the dvh height and the safe-area insets for every overlay in the app; the layer only has to
      // get out of their way.
      sheet: 'items-stretch justify-stretch p-0',
      fullscreen: 'items-stretch justify-stretch p-0',
    },
    // The classes assign the bands from tokens.css (app/styles/components/primitives.css); no overlay in
    // this app may name a band with a literal. `page` is the lowest of the three: an intercepted route
    // presented as an overlay stands in for its page, so it ranks UNDER the drawers and dialogs it opens.
    layer: {
      page: 'overlay-layer-page',
      drawer: 'overlay-layer-drawer',
      modal: 'overlay-layer-modal',
    },
    scrim: {
      default: 'bg-[var(--color-scrim)]',
      soft: 'bg-[var(--color-scrim-soft)]',
    },
  },
  defaultVariants: { presentation: 'center', layer: 'modal', scrim: 'default' },
});

type DialogPresentation = NonNullable<VariantProps<typeof dialogOverlayVariants>['presentation']>;
type DialogLayer = NonNullable<VariantProps<typeof dialogOverlayVariants>['layer']>;
type DialogScrim = NonNullable<VariantProps<typeof dialogOverlayVariants>['scrim']>;

function DialogOverlay({
  className = '',
  presentation = 'center',
  layer = 'modal',
  scrim = 'default',
  style,
  ...props
}: React.ComponentProps<'div'> & { presentation?: DialogPresentation; layer?: DialogLayer; scrim?: DialogScrim }) {
  return (
    <div
      data-slot="dialog-overlay"
      data-scrim={scrim}
      className={cn(dialogOverlayVariants({ presentation, layer, scrim }), className)}
      style={{
        // Radix's modal content sets `pointer-events: none` on <body> so that nothing outside it can be
        // pressed, and re-enables them on itself. This layer is a child of <body> and would inherit the
        // block, which would kill the click-outside-to-close on the backdrop — silently, because jsdom
        // does not implement pointer-events. Opting back in is exactly what Radix's own `Dialog.Overlay`
        // does for the same reason.
        pointerEvents: 'auto',
        // A centered dialog keeps its 1rem breathing room, widened to the safe area where the device has
        // one — in landscape the notch is on a SIDE, so a fixed inset is not enough on its own. The other
        // presentations get their insets from `.overlay-surface`.
        ...(presentation === 'center'
          ? {
            paddingBlock: 'max(var(--spacing) * 4, var(--safe-top)) max(var(--spacing) * 4, var(--safe-bottom))',
            paddingInline: 'max(var(--spacing) * 4, var(--safe-left)) max(var(--spacing) * 4, var(--safe-right))',
          }
          : null),
        ...style,
      }}
      {...props}
    />
  );
}

/** The surface itself. `presentation` picks the shape, `size` the room a centered window takes and
 *  `width` the room a drawer takes; the two size axes are compound rather than free because a drawer has
 *  no use for `max-w-lg` and a centered window has no use for a drawer width.
 *
 *  `.overlay-surface` is what `app/styles/components/primitives.css` paints (background, border colour,
 *  raised shadow) and what dresses the two phone presentations geometrically. The variants below own the
 *  shape and nothing else: border WIDTH and which edges are drawn is geometry; the colour of that border
 *  is not.
 *
 *  dvh throughout: a mobile browser's collapsing toolbar makes `vh` taller than the screen actually is,
 *  which put the footer of every one of these under the browser chrome.
 *
 *  `chrome` is the one axis that asks whether this surface IS a panel. `card` — the default, and what
 *  every dialog, drawer and sheet in the app wears — carries the shared material, the drawn edge and the
 *  size a panel needs room for. `bare` declines all three: a surface whose content IS the frame (the chat
 *  image lightbox) must not paint a ground, an edge, a shadow or a `max-w-lg` over the picture the reader
 *  clicked, or the lightbox is a card again. Everything else is shared and unchanged: the same primitive,
 *  the same focus trap, Escape, layer order, inert isolation and scroll lock. */
// The surface owns its shape; ToastSurface keeps the dock inside its focus scope in both layouts.
const dialogSurfaceVariants = cva('relative flex flex-col focus:outline-none', {
  variants: {
    presentation: {
      center: 'animate-pop-in',
      drawer: 'animate-drawer-in h-full rounded-l-lg border-l',
      sheet: 'min-h-0 w-full',
      fullscreen: 'animate-pop-in relative min-h-0 w-full',
    },
    chrome: { card: 'overlay-surface', bare: '' },
    size: { sm: '', md: '', lg: '', xl: '', page: '' },
    width: { default: '', wide: '' },
  },
  compoundVariants: [
    // The drawn edge and the room both belong to a PANEL, so both are gated on the chrome: a bare surface
    // takes exactly what its content takes.
    { presentation: 'center', chrome: 'card', class: 'rounded-lg border' },
    { presentation: 'fullscreen', chrome: 'card', class: 'border' },
    { presentation: 'fullscreen', chrome: 'bare', class: 'h-full' },
    { presentation: 'center', chrome: 'bare', class: 'relative h-full w-full items-center justify-center' },
    { presentation: 'center', chrome: 'card', size: 'lg', class: 'h-[88dvh] w-[92vw] max-w-360' },
    // The READING frame, taken by an intercepted page that is a stack of records rather than a data
    // surface — both Settings and Account ask for `PageOverlay frame="reading"` — and by the resource-history
    // window a metric opens, from Settings or from a Project alike, so the two surfaces are one window.
    // `lg` beside it is the DATA window that wants every pixel a monitor has (a log table, a diagnostics
    // run), so it grows to 90rem by 88dvh.
    //
    // The width is not a number of its own. `--content-max` (app/styles/tokens.css) is the single
    // authority for how wide a workspace may grow; every shell and content surface already reads it and
    // a skin moves it to suit its own measure, so restating a cap here is the one thing that token
    // forbids. The height IS stated, because this frame has to hold still while sections of very
    // different lengths are swapped through it.
    // ONE measurement of the page frame, whatever the chrome: the card page is the reading window (Settings
    // and Account as intercepted overlays, the resource-history window), and a BARE page is a window whose
    // content is its own surface (the terminal). Declared after the bare default above, so a bare page's
    // height and width win the merge; a bare overlay that asks for no size (the chat image lightbox) keeps
    // `h-full w-full` from that default and is unchanged.
    { presentation: 'center', chrome: ['bare', 'card'], size: 'page', class: 'h-[min(88dvh,--spacing(200))] w-[92vw] max-w-[var(--content-max)]' },
    { presentation: 'center', chrome: 'card', size: 'xl', class: 'max-h-[90dvh] w-full max-w-2xl' },
    { presentation: 'center', chrome: 'card', size: 'md', class: 'max-h-[88dvh] w-full max-w-lg' },
    { presentation: 'center', chrome: 'card', size: 'sm', class: 'max-h-[80dvh] w-full max-w-md' },
    { presentation: 'drawer', width: 'default', class: 'w-[min(--spacing(152),calc(100vw-var(--spacing)*12))]' },
    { presentation: 'drawer', width: 'wide', class: 'w-[min(--spacing(288),calc(100vw-var(--spacing)*12))]' },
  ],
  defaultVariants: { presentation: 'center', chrome: 'card', size: 'md', width: 'default' },
});

function DialogContent({
  className = '',
  presentation = 'center',
  // Whether this surface IS a panel. `card` is every dialog, drawer and sheet in the app. `bare` is one
  // whose content is the whole frame — the chat image lightbox, where a ground, an edge, a cast shadow and
  // the room a panel reserves for a title would all be a card drawn over the picture the reader clicked.
  // `bare` therefore also declines the drawn edge and the size caps (see `dialogSurfaceVariants`) and stops
  // being a click target itself (see `style` below). What it keeps is everything that was never visual: the
  // same primitive, the same focus trap, Escape, layer order, inert isolation and scroll lock.
  chrome = 'card',
  size = 'md',
  width = 'default',
  style,
  onEscapeKeyDown,
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & VariantProps<typeof dialogSurfaceVariants>) {
  return (
    <DialogPrimitive.Content
      data-slot="dialog-content"
      // Radix sets `role="dialog"` but leaves `aria-modal` to the caller, because a non-modal Radix
      // dialog uses the same content part. Every dialog in this app isolates the page behind it, so it
      // is a modal one and says so.
      aria-modal="true"
      data-presentation={presentation}
      // Stated on the DOM as well as in the class list, so a reader inspecting the surface can see which
      // of the two it was built as.
      data-chrome={chrome}
      // `focus:outline-none` on the surface itself: the overlay focuses this element on open so the focus
      // trap and screen readers have an anchor, but it is `tabIndex={-1}` and not interactive, so the
      // browser's ring around the whole window says nothing. Opening a dialog from the keyboard — a slash
      // command, for instance — made `:focus-visible` match and drew a bright outline around the entire
      // dialog that vanished on the first click inside. Controls INSIDE keep their own rings.
      //
      // A bare surface takes the whole padded viewport, and it must not swallow the presses that belong to
      // the backdrop behind it. This has to be inline because Radix states `pointer-events: auto` inline on
      // a modal content element (`@radix-ui/react-dismissable-layer`), which a utility class cannot beat;
      // the class silently losing that fight is what made a click beside the picture do nothing. A surface
      // whose content opts back in is then exactly the split the reader expects: the picture is clickable,
      // the space around it is the scrim.
      style={chrome === 'bare' ? { pointerEvents: 'none', ...style } : style}
      className={cn(dialogSurfaceVariants({ presentation, chrome, size, width }), className)}
      {...props}
      onEscapeKeyDown={(event) => {
        // Escape typed into a toast (its actions live inside this surface's focus scope) belongs to the
        // toast: it must not close the dialog the toast is about.
        if (isToastDockEvent(event)) {
          event.preventDefault();
          return;
        }
        // An element marked `data-keyboard-claimed` owns every key typed into it, Escape included: leave
        // the key unconsumed so it reaches that element, and do not let it dismiss the dialog. A controlled
        // remote desktop is marked by `useVncSurface` only while it really has the keyboard (connected and
        // interactive); a plugin secret replacement input is marked while Escape means "cancel".
        if (event.target instanceof Element && event.target.closest('[data-keyboard-claimed]')) {
          event.preventDefault();
          return;
        }
        // Radix selects the top layer and prevents the browser default, but does not consume the
        // native key. A portal still bubbles through its React ancestors and global bubble listeners.
        event.stopPropagation();
        onEscapeKeyDown?.(event);
      }}
    >
      {/* `asChild` hands the surface to a single element the caller owns, so there is nowhere to add the outlet:
          that owner renders `<ToastSurface />` as the last child of its element (the contract guard checks). */}
      {props.asChild ? children : <ToastSurface>{children}</ToastSurface>}
    </DialogPrimitive.Content>
  );
}

/** The one header every overlay in this app shares: an optional 40px icon tile, the title with a meta line
 *  under it, call-site actions and the close control on the SAME row, and an optional tabs slot under
 *  that row. Plain markup with no Radix coupling, so the alert dialog can wear the same chrome —
 *  `ConfirmDialog.tsx` composes this too.
 *
 *  The close control is the shared `icon` size step — 36px, 44px on a coarse pointer — so it matches the
 *  actions beside it instead of declaring a 32px box of its own. An overlay header is a page header: a
 *  record header that wants the compact step moves its whole action group there, not the close alone.
 *
 *  The header owns the only hairline between the title area and the body. `tabs` therefore lives inside
 *  it and is pulled down one pixel (`-mb-px`) so the active tab's underline sits on that hairline; the
 *  `line` Segmented's own `border-b` is switched off through the `dialog-tabs` slot, which is what keeps
 *  it from drawing a second line. The slot also enlarges the strip into header tabs (section-title text,
 *  a taller hit area, a brand underline under the open tab); that look lives in primitives.css, keyed to
 *  `data-slot="dialog-tabs"`.
 *
 *  The ids are passed in rather than taken from a `Dialog.Title`: the same header serves two different
 *  Radix scopes, and a `Dialog.Title` rendered inside an `AlertDialog` reads the wrong context. */
function DialogHeader({
  title,
  titleId,
  titleAside,
  description,
  descriptionId,
  icon: Icon,
  iconNode,
  actions,
  tabs,
  closeLabel,
  closeDisabled = false,
  onClose,
}: {
  title: string;
  titleId: string;
  /** A quiet mark that belongs to the title itself — the help affordance of the surface it names. It shares
   *  the title's line, because it explains the words beside it and not the window; the header's `actions`
   *  row is for controls that DO something, and there it would read as one more of them. */
  titleAside?: React.ReactNode;
  /** The meta line under the title. A node so a status dot can sit in front of the words (`DetailMeta`). */
  description?: React.ReactNode;
  descriptionId?: string;
  icon?: LucideIcon;
  /** A pre-rendered brand mark drawn in the same tile instead of a Lucide `icon`. */
  iconNode?: React.ReactNode;
  actions?: React.ReactNode;
  /** A tab strip (a `line` Segmented) drawn inside the header, above its hairline. */
  tabs?: React.ReactNode;
  closeLabel: string;
  closeDisabled?: boolean;
  onClose: () => void;
}) {
  return (
    <div data-slot="dialog-header" className={cn('shrink-0 border-b border-border px-4 pt-5 sm:px-6', tabs ? 'pb-0' : 'pb-4')}>
      {/* One row on a window that has room for both: mark, title and the actions beside it. On a phone
          the actions take a row of their own under the title and the close stays at the top right, so
          the title keeps a readable line instead of being squeezed to nothing between a mark and two
          labelled buttons (owner report: a 320px workflow modal whose title measured 0px wide). The
          title's 10rem basis decides the fold by readability, never by whichever box shrank last. */}
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        {iconNode || Icon ? (
          <span data-slot="dialog-mark" className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border">
            {iconNode ?? (Icon ? <Icon className="size-4.5 text-foreground" aria-hidden /> : null)}
          </span>
        ) : null}
        <div className="min-w-0 grow basis-40">
          {/* The title and its own mark share one line: a help affordance explains the words it stands
              beside, so it rides the title's row instead of taking a row of the header for itself. The
              h2 keeps the truncation and the mark never shrinks, so a long name ellipses rather than
              squeezing the "?" to nothing. */}
          <div className="flex min-w-0 items-center gap-2">
            <h2 id={titleId} data-slot="dialog-title" className="truncate text-section-title text-foreground">{title}</h2>
            {titleAside ? <span data-slot="dialog-title-aside" className="flex shrink-0 items-center">{titleAside}</span> : null}
          </div>
          {description ? (
            // `wrap-anywhere`: the meta is often one long token (a host project's path). Bare text in this flex
            // line is an anonymous item that cannot shrink below its longest word, so without it the path
            // ran on under the actions and past a phone's edge; now it breaks inside the title column.
            <div id={descriptionId} data-slot="dialog-description" className="flex flex-wrap items-center gap-x-1.5 text-meta text-muted-foreground wrap-anywhere">{description}</div>
          ) : null}
        </div>
        {actions ? (
          <div data-slot="dialog-actions" className="order-last flex w-full flex-wrap items-center justify-end gap-1 sm:order-none sm:w-auto">{actions}</div>
        ) : null}
        {/* The close keeps a box of its own on the right edge: it is the header's control even on a phone, and
            the title column's contract is that it ends before whatever stands beside it. */}
        <div data-slot="dialog-close" className="ml-auto flex shrink-0 items-center">
          <Button type="button" variant="ghost" size="icon" aria-label={closeLabel} disabled={closeDisabled} onClick={onClose}>
            <X className="size-4" aria-hidden />
          </Button>
        </div>
      </div>
      {tabs ? (
        <div data-slot="dialog-tabs" className="-mb-px mt-4 [&_.segmented]:-m-1 [&_.segmented]:w-[calc(100%+var(--spacing)*2)] [&_.segmented]:max-w-none [&_.segmented]:border-b-0 [&_.segmented]:p-1">{tabs}</div>
      ) : null}
    </div>
  );
}

// Only what a wrapper composes: `dialogSurfaceVariants` is shared with `alert-dialog.tsx` so a
// confirmation and a centered dialog stay one object at two sizes.
export { Dialog, DialogContent, DialogHeader, DialogOverlay, dialogSurfaceVariants };
